import { useId, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import type { MoleculeFacts, MoleculeSummary, PublishedStatement } from '@peruselab/api-client/molecules';
import { Icon } from '../../components/Icon';
import { MoleculeThumbnail } from '../../components/MoleculeThumbnail';
import { pubChemUrl } from '../chemistry/molecule/factsheetText';
import { usePublicAccess } from '../public/publicAccess';
import { shortSourceName } from './propertyCards';

const PICTURE_SIZE = 200;
const DESCRIPTION_MAX = 160;

/** The first sentence of the first About statement, short enough for one line under the name. */
function oneLineDescription(
  about: readonly PublishedStatement[] | null | undefined
): { text: string; source: string } | null {
  const first = about?.[0];
  if (!first) return null;
  const sentence = /^.*?[.!?](?=\s|$)/s.exec(first.text)?.[0] ?? first.text;
  const text = sentence.length > DESCRIPTION_MAX ? `${sentence.slice(0, DESCRIPTION_MAX - 1).trimEnd()}…` : sentence;
  return { text, source: shortSourceName(first.source) };
}

/**
 * #929/#930 (epic #934): the Monograph's hero, shared by the Cards and Sheet views -- the structure
 * on its white tile, the name and a one-line description with its source, identifier chips, and
 * the SMILES (Cards), and "Add what you know" / "Open in workspace" (signed in) or "Sign in to add".
 */
export function MonographHero({
  summary,
  facts,
  name,
  formula,
  weight,
  onAddValue,
  about,
  smiles
}: {
  readonly summary: MoleculeSummary;
  readonly facts: MoleculeFacts | null;
  readonly name: string | null;
  readonly formula: ReactNode;
  readonly weight: string | null;
  /** Opens the add-value form (signed in) -- the hero signs a reader in first otherwise. */
  readonly onAddValue: () => void;
  /** #947: About as assertions, when imported -- the description line is its first. */
  readonly about?: readonly PublishedStatement[] | null;
  /** #1162: the SMILES line under the chips -- the Cards view's; the Sheet lists it in Identifiers. */
  readonly smiles?: ReactNode;
}) {
  const { t } = useTranslation();
  const { signedIn } = usePublicAccess();
  const titleId = useId();
  const description = oneLineDescription(about ?? (facts?.status === 'ready' ? facts.about : null));
  const cid = summary.pubchemCid ? Number(summary.pubchemCid) : null;
  return (
    <section className="monograph-hero" aria-labelledby={titleId}>
      <div className="monograph-hero-inner">
        <figure className="monograph-hero-picture">
          <MoleculeThumbnail
            moleculeId={summary.id}
            size={PICTURE_SIZE}
            label={name ? t('moleculeThumbnail.structureOf', { name }) : t('moleculePage.pictureLabel')}
          />
        </figure>
        <div className="monograph-hero-text">
          <p className="monograph-eyebrow">{t('monograph.eyebrow')}</p>
          <h1 id={titleId} dir="auto" title={name ?? undefined}>
            {name ?? formula ?? t('moleculePage.unnamed')}
          </h1>
          {description ? (
            <p className="monograph-hero-description">
              <span lang="en" dir="auto">
                {description.text}
              </span>{' '}
              <span className="monograph-hero-source">({description.source})</span>
            </p>
          ) : null}
          <ul className="monograph-identifier-chips" aria-label={t('moleculePage.identifiers')}>
            {summary.molecularFormula ? <li>{formula}</li> : null}
            {weight ? <li>{t('moleculePage.weightValue', { weight })}</li> : null}
            {summary.cas ? <li dir="ltr">{t('monograph.cas', { cas: summary.cas })}</li> : null}
            {cid ? (
              <li>
                <a href={pubChemUrl(cid)} target="_blank" rel="noopener noreferrer" className="button-with-icon">
                  <span dir="ltr">{t('monograph.pubchem', { cid })}</span>
                  <Icon name="openInNew" size="0.85rem" />
                </a>
              </li>
            ) : null}
          </ul>
          {smiles ? (
            <div className="monograph-hero-smiles">
              <span className="monograph-hero-smiles-label">{t('moleculePage.smiles')}</span>
              {smiles}
            </div>
          ) : null}
        </div>
        <div className="monograph-hero-actions">
          <button
            type="button"
            className="monograph-hero-primary icon-button"
            onClick={onAddValue}
            aria-label={signedIn ? t('monograph.addWhatYouKnow') : t('properties.signInToAdd')}
            title={signedIn ? t('monograph.addWhatYouKnow') : t('properties.signInToAdd')}
          >
            <Icon name={signedIn ? 'add' : 'lock'} size="1.1rem" />
          </button>
          {signedIn ? (
            <Link
              to={`/draw?addMolecule=${encodeURIComponent(summary.id)}`}
              className="monograph-hero-secondary icon-button"
              aria-label={t('monograph.openInWorkspace')}
              title={t('monograph.openInWorkspace')}
            >
              <Icon name="edit" size="1.1rem" />
            </Link>
          ) : null}
        </div>
      </div>
    </section>
  );
}
